import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"
import GatsbyImage from "gatsby-image"

// Components
import Seo from "../components/Seo"
import TitleDescriptionImage from "../components/slices/TitleDescriptionImage"
import QuoteSlice from "../components/slices/QuoteSlice"
import TextDescriptionColumns from "../components/slices/TextDescriptionColumns"
import StackColumnsContent from "../components/slices/StackColumnsContent"
import CTASlice from "../components/slices/CTASlice"

// Styles
import colors from "../styles/colors"
import { fonts } from "../styles/fonts"
import { fontWeights } from "../styles/textStyles"
import mq from "../styles/breakpoints"

const CaseStudyTemplatePage = ({ data }: any) => {
  const {
    data: templateData,
    uid,
    lang,
    first_publication_date,
    last_publication_date,
  } = data.prismicCaseStudiesTemplate

  const title = templateData.title?.text || ""
  const description = templateData.description || ""
  const heroImage = templateData.hero_image
  const companyName = templateData.company_name || ""
  const companySector = templateData.company_sector || ""
  const body = templateData.body || []

  const ctaSlices = body.filter(
    (slice: any) =>
      slice.slice_type === "team_member_v2" ||
      slice.slice_type === "services_cards" ||
      slice.slice_type === "resources_cards" ||
      slice.slice_type === "deep_dive_cards" ||
      slice.slice_type === "case_studies_cards"
  )

  return (
    <>
      <Seo
        title={templateData.meta_title || ""}
        description={templateData.meta_description || ""}
        slug={`cases/${uid}`}
        lang={lang}
        datePublished={first_publication_date}
        dateModified={last_publication_date}
      />
      <PageContainer>
        {/* HERO SECTION */}
        <HeroSection>
          <HeroImageWrapper>
            {heroImage?.fluid ? (
              <HeroImageInner>
                <GatsbyImage
                  fluid={heroImage.fluid}
                  alt={heroImage.alt || title}
                />
              </HeroImageInner>
            ) : heroImage?.url ? (
              <HeroImageFallback
                src={heroImage.url}
                alt={heroImage.alt || title}
              />
            ) : null}
            <HeroGradient />
          </HeroImageWrapper>
          <HeroContent>
            <HeroInner>
              <Badge>Case study</Badge>
              <HeroTitle>{title}</HeroTitle>
              <HeroDescription>{description}</HeroDescription>
              {(companyName || companySector) && (
                <HeroTags>
                  {companyName && <HeroTag>{companyName}</HeroTag>}
                  {companySector && <HeroTag>{companySector}</HeroTag>}
                </HeroTags>
              )}
            </HeroInner>
          </HeroContent>
        </HeroSection>

        {/* CONTENT SLICES */}
        <ContentSection>
          <ContentInner>
            <SlicesWrapper>
              {body.map((slice: any, index: number) => (
                <React.Fragment key={index}>
                  {slice.slice_type === "titledescriptionimage" && (
                    <TitleDescriptionImage
                      primary={{
                        align: slice.primary.align,
                        description1: slice.primary.description1.html,
                        title: slice.primary.title.raw,
                        subtitle: slice.primary.subtitle,
                        image_position: slice.primary.image_position,
                        image: slice.primary.image,
                      }}
                    />
                  )}
                  {slice.slice_type === "quote" && (
                    <QuoteSlice
                      primary={{
                        title: slice.primary.title.raw,
                        subtitle: slice.primary.subtitle,
                      }}
                    />
                  )}
                  {slice.slice_type === "textdescriptioncolumns" && (
                    <TextDescriptionColumns
                      primary={{
                        columnsPerRow: slice.primary.columns_per_row,
                      }}
                      items={slice.items.map((item: any) => ({
                        title: item.title.raw,
                        description1: item.description1.html,
                      }))}
                    />
                  )}
                  {slice.slice_type === "stackcolumnscontent" && (
                    <StackColumnsContent
                      items={slice.items.map((item: any) => ({
                        image: item.image,
                        title: item.title.raw,
                        description1: item.description1.html,
                      }))}
                    />
                  )}
                </React.Fragment>
              ))}
            </SlicesWrapper>

            {ctaSlices.map((slice: any, index: number) => (
              <CTASlice key={index} slice={slice} />
            ))}
          </ContentInner>
        </ContentSection>
      </PageContainer>
    </>
  )
}

export const query = graphql`
  query caseStudyTemplate($uid: String!, $locale: String!) {
    prismicCaseStudiesTemplate(uid: { eq: $uid }, lang: { eq: $locale }) {
      uid
      lang
      first_publication_date
      last_publication_date
      data {
        meta_title
        meta_description
        title {
          text
        }
        description
        hero_image {
          alt
          url
          fluid {
            aspectRatio
            base64
            sizes
            src
            srcSet
            srcSetWebp
            srcWebp
          }
        }
        company_name
        company_sector
        body {
          ... on PrismicCaseStudiesTemplateBodyTitledescriptionimage {
            id
            slice_type
            primary {
              subtitle
              title {
                raw
              }
              align
              description1 {
                html
              }
              image_position
              image {
                fluid {
                  aspectRatio
                  base64
                  sizes
                  src
                  srcSet
                  srcSetWebp
                  srcWebp
                }
                alt
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyQuote {
            id
            slice_type
            primary {
              subtitle
              title {
                raw
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyTextdescriptioncolumns {
            id
            slice_type
            primary {
              columns_per_row
            }
            items {
              description1 {
                html
              }
              title {
                raw
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyStackcolumnscontent {
            id
            slice_type
            items {
              description1 {
                html
              }
              title {
                raw
              }
              image {
                alt
                fluid {
                  aspectRatio
                  base64
                  src
                  srcSet
                  srcSetWebp
                  srcWebp
                  sizes
                }
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyDeepDiveCards {
            id
            slice_type
            primary {
              button_label
              button_link
              deep_dive_description
              deep_dive_heading {
                raw
                text
              }
            }
            items {
              deep_dive_template {
                id
                type
                uid
                document {
                  ... on PrismicDeepDiveTemplate {
                    id
                    data {
                      heading {
                        text
                        raw
                      }
                      description
                      heading_color
                      foreground_color
                      background_color
                    }
                  }
                }
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyServicesCards {
            id
            slice_type
            primary {
              services_cards_button_label
              services_cards_button_link
              services_cards_heading {
                raw
              }
            }
            items {
              services_cards_template {
                document {
                  ... on PrismicServicesTemplate {
                    id
                    uid
                    data {
                      title
                      service_type
                      service_level
                      service_type_color
                      heading
                      description
                      thumbnail_image {
                        alt
                        fluid {
                          aspectRatio
                          base64
                          sizes
                          src
                          srcSet
                          srcSetWebp
                          srcWebp
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyResourcesCards {
            id
            slice_type
            primary {
              resources_cards_button_link
              resources_cards_heading {
                raw
              }
              resources_cards_label
            }
            items {
              resources_cards_template {
                document {
                  ... on PrismicResourceTemplate {
                    uid
                    data {
                      heading {
                        text
                      }
                      description {
                        text
                      }
                      image {
                        alt
                        url
                        fluid {
                          aspectRatio
                          base64
                          sizes
                          src
                          srcSet
                          srcSetWebp
                          srcWebp
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyCaseStudiesCards {
            id
            slice_type
            primary {
              button_label
              case_studies_heading {
                raw
                text
              }
            }
            items {
              case_studies_template {
                id
                type
                uid
                document {
                  ... on PrismicCaseStudiesTemplate {
                    id
                    uid
                    data {
                      title {
                        text
                      }
                      company_name
                      company_sector
                      hero_image {
                        alt
                        url
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicCaseStudiesTemplateBodyTeamMemberV2 {
            id
            slice_type
            items {
              team_member {
                document {
                  ... on PrismicTeamMember {
                    id
                    data {
                      name
                      role
                      photo {
                        alt
                        fluid {
                          aspectRatio
                          base64
                          sizes
                          src
                          srcSet
                          srcSetWebp
                          srcWebp
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export default CaseStudyTemplatePage

/* ============================================
   STYLED COMPONENTS
   ============================================ */

const PageContainer = styled.div`
  background-color: ${colors.main["900"]};
`

/* --- Hero Section --- */

const NAV_HEIGHT_MOBILE = 56
const NAV_HEIGHT_DESKTOP = 72
const HERO_HEIGHT = 525

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  height: ${NAV_HEIGHT_MOBILE + HERO_HEIGHT}px;
  overflow: hidden;

  ${mq.from.M`
    height: ${NAV_HEIGHT_DESKTOP + HERO_HEIGHT}px;
  `}
`

const HeroImageWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`

const HeroImageInner = styled.div`
  width: 100%;
  height: 100%;

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }

  img {
    object-fit: cover;
  }
`

const HeroImageFallback = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const HeroGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(249, 248, 252, 0) 0%,
    ${colors.main["900"]} 100%
  );
`

const HeroContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  padding: ${NAV_HEIGHT_MOBILE + 40}px 27px 40px;

  ${mq.from.S`
    padding: ${NAV_HEIGHT_MOBILE + 40}px 48px 40px;
  `}

  ${mq.from.M`
    padding: ${NAV_HEIGHT_DESKTOP + 40}px 189px 40px;
  `}
`

const HeroInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  width: 100%;
  max-width: 1024px;
`

const Badge = styled.h4`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background-color: ${colors.purpleLight};
  border-radius: 8px;
  font-family: ${fonts.primary};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  line-height: 16px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${colors.white};

  ${mq.from.M`
    font-size: 16px;
  `}
`

const HeroTitle = styled.h1`
  margin: 0;
  font-family: ${fonts.primary};
  font-size: 40px;
  font-weight: ${fontWeights.bold};
  line-height: 54px;
  letter-spacing: -2px;
  text-align: center;
  color: ${colors.main["100"]};

  ${mq.from.M`
    font-size: 64px;
    line-height: 76px;
  `}
`

const HeroDescription = styled.p`
  margin: 0;
  font-family: ${fonts.primary};
  font-size: 18px;
  font-weight: ${fontWeights.regular};
  line-height: 28px;
  letter-spacing: -0.18px;
  text-align: center;
  color: ${colors.main["100"]};

  ${mq.from.M`
    font-size: 20px;
    line-height: 32px;
    letter-spacing: -0.2px;
  `}
`

const HeroTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`

const HeroTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: 1px solid ${colors.purpleLight};
  border-radius: 50px;
  font-family: ${fonts.primary};
  font-size: 12px;
  font-weight: ${fontWeights.regular};
  line-height: 16px;
  letter-spacing: -0.12px;
  color: ${colors.purpleLight};
  white-space: nowrap;

  ${mq.from.M`
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.14px;
  `}
`

/* --- Content Section --- */

const ContentSection = styled.section`
  padding: 80px 0;

  ${mq.from.M`
    padding: 80px 0;
  `}
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 96px;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;

  ${mq.from.S`
    padding: 0 48px;
  `}

  ${mq.from.L`
    padding: 0 80px;
  `}

  ${mq.from.XL`
    padding: 0 80px;
    max-width: 1440px;
  `}
`

const SlicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 96px;

  ${mq.from.L`
    padding: 0 109px;
  `}
`
